<template>
  <div class="pagination-wrapper">
    <div v-if="hasRange" class="pagination-info">
      {{ rangeLabel }}
    </div>
    <div class="paginate">
      <button
        class="prev-button"
        @click="prevStep"
        :disabled="isDisabledPrevButton"
      >{{ prevButtonPlaceholder }}</button>
      <button
        class="paginateItem"
        v-for="item of paginateItems"
        :class="{'active': item === activePage}"
        @click="itemHandler"
        :key="item"
      >{{ item }}</button>
      <button
        class="next-button"
        @click="nextStep"
        :disabled="isDisabledNextButton"
      >{{ nextButtonPlaceholder }}</button>
    </div>
  </div>
</template>

<script>
const VISIBLE_PAGES = 5;

export default {
  name: 'Paginate',
  data() {
    return {
      startCount: 1
    };
  },
  props: {
    pagesCount: Number,
    prevButtonPlaceholder: String,
    nextButtonPlaceholder: String,
    currentPage: [Number, String],
    prevRoutePage: [Number, String],
    totalCount: {
      type: Number,
      default: 0
    },
    pageSize: {
      type: Number,
      default: 15
    }
  },
  computed: {
    pageIndex() {
      const page = Number(this.currentPage);

      if (Number.isNaN(page) || page < 0) {
        return 0;
      }

      return page;
    },
    rangeFrom() {
      if (!this.totalCount) {
        return 0;
      }

      if (this.pageIndex === 0) {
        return 1;
      }

      return this.pageIndex * this.pageSize;
    },
    rangeTo() {
      if (!this.totalCount) {
        return 0;
      }

      return Math.min((this.pageIndex + 1) * this.pageSize, this.totalCount);
    },
    hasRange() {
      return this.totalCount > 0;
    },
    rangeLabel() {
      return this.$t('message.pagination.range', {
        from: this.rangeFrom,
        to: this.rangeTo,
        total: this.totalCount
      });
    },
    paginateItems() {
      const totalPages = this.pagesCount || 0;

      if (!totalPages) {
        return [];
      }

      const end = Math.min(this.startCount + VISIBLE_PAGES - 1, totalPages);

      return Array.from({ length: end - this.startCount + 1 }, (_, index) => this.startCount + index);
    },
    isDisabledPrevButton() {
      return this.startCount <= 1;
    },
    isDisabledNextButton() {
      const totalPages = this.pagesCount || 0;

      return this.startCount + VISIBLE_PAGES - 1 >= totalPages;
    },
    activePage() {
      return this.getPageNumber(this.currentPage);
    }
  },
  methods: {
    itemHandler(e) {
      return this.$emit('choice', Number(e.target.textContent) - 1);
    },
    nextStep() {
      const totalPages = this.pagesCount || 0;
      const maxStart = Math.max(1, totalPages - VISIBLE_PAGES + 1);

      if (this.startCount < maxStart) {
        this.startCount++;
      }
    },
    prevStep() {
      if (this.startCount > 1) {
        this.startCount--;
      }
    },
    getPageNumber(value) {
      const page = Number(value);

      if (Number.isNaN(page) || page < 0) {
        return 1;
      }

      return page + 1;
    },
    syncStartCount(page) {
      const activePage = this.getPageNumber(page);
      const totalPages = this.pagesCount || 0;

      if (totalPages <= VISIBLE_PAGES) {
        this.startCount = 1;
        return;
      }

      let start = activePage - Math.floor(VISIBLE_PAGES / 2);

      if (start < 1) {
        start = 1;
      }

      if (start + VISIBLE_PAGES - 1 > totalPages) {
        start = totalPages - VISIBLE_PAGES + 1;
      }

      this.startCount = start;
    },
    setPagination() {
      const currentPage = this.prevRoutePage !== undefined ? this.prevRoutePage : this.currentPage;

      this.syncStartCount(currentPage);
    }
  },
  watch: {
    currentPage(value) {
      this.syncStartCount(value);
    },
    pagesCount() {
      this.syncStartCount(this.currentPage);
    }
  },
  created() {
    this.setPagination();
  }
};
</script>

<style lang="scss" scoped>
  .pagination-wrapper {
    width: 100%;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .pagination-info {
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    color: #6B6B6B;
  }

  .paginate {
    height: 32px;
    display: flex;
    align-items: center;

    button {
      height: 30px;
      width: 30px;
      margin-right: 8px;
      display: flex;
      justify-content: center;
      align-items: center;
      font-weight: 400;
      font-size: 14px;
      line-height: 22px;
      border-radius: 5px;
      border: none;
      background-color: white;
      cursor: pointer;
      box-shadow: 0px 0px 8px -3px rgba(34, 60, 80, 0.2);
    }

    .prev-button,
    .next-button {
      min-width: 30px;
      width: auto;
      padding: 0 3%;
    }

    .active {
      background-color: #41BF7B;
      color: white;
    }
  }
</style>
